'use client';

import { EventEntryFragment } from '@/__generated__/graphql';
import EntryMeta, { MetaValues } from '@/components/EntryMeta';
import Performance from '@/components/Performance';
import Box from '@/components/ui/Box';
import Btn from '@/components/ui/Btn';
import Flex from '@/components/ui/Flex';
import Txt from '@/components/ui/Txt';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { EVENT_CARD_FRAGMENT, VENUE_CARD_FRAGMENT } from '@/gql/fragments/entryCards.gql';
import { parseVenue } from '@/lib/parsers/categories';
import { parseEventInfoFragment } from '@/lib/parsers/common/parseEventInfo';
import { parseEventPriceFragment } from '@/lib/parsers/common/parseEventPrice';
import { parseEventTimeFragment } from '@/lib/parsers/common/parseEventTime';
import { AppGlobals } from '@/lib/parsers/globals';
import useShortlistStore, { useIsShortlisted } from '@/lib/store/shortlist';
import { fmtCategoryAddress } from '@/lib/utils/format';
import { cleanHtml } from '@/lib/utils/htmlHelpers';
import typography from '@/theme/partials/typography.css';
import { getFragmentData } from '@liquorice/gql-utils/src/types/fragment-masking';
import { firstNonNullable, parseUri } from '@liquorice/utils';
import { useTranslations } from 'next-intl';

type EventLayoutSidebarProps = EventEntryFragment & {
  globals?: AppGlobals | null;
};

const EventLayoutSidebar = ({ globals, ...props }: EventLayoutSidebarProps) => {
  const t = useTranslations();
  const eventTime = parseEventTimeFragment(props.eventTime);
  const eventPrice = parseEventPriceFragment(props.eventPrice);
  const eventInfo = parseEventInfoFragment(props.eventInfo);
  const pricing = cleanHtml(globals?.pricingInformation?.content);
  const addressFull = fmtCategoryAddress(props.venueCategory);
  const addressLine = fmtCategoryAddress(props.venueCategory, true);

  const card = getFragmentData(EVENT_CARD_FRAGMENT, props);
  const toggleEvent = useShortlistStore((s) => s.toggleEvent);
  const isShortlisted = useIsShortlisted(card);

  const shortlistButtonText = isShortlisted
    ? t('actions.removeFromShortlist')
    : t('actions.addToShortlist');

  const venue = parseVenue(props.venueCategory);

  const venueSingle = firstNonNullable(venue?.venueSingle);
  const venueRef =
    venueSingle?.__typename === 'venue_Entry'
      ? getFragmentData(VENUE_CARD_FRAGMENT, venueSingle)
      : undefined;

  const venueRefBase = getFragmentData(ENTRY_BASE_FRAGMENT, venueRef);
  const venueRefUri = parseUri(venueRefBase?.uri);

  const meta: MetaValues = [
    eventTime.length > 0
      ? {
          label: t('common.performances'),
          value: <Performance key="event_performance" items={eventTime} />,
        }
      : undefined,
    eventPrice.length > 0
      ? {
          label: 'Ticket Prices',
          value: (
            <Box key="event_price">
              {eventPrice.map((item, i) => {
                return (
                  <Txt
                    key={i}
                    as="p"
                    variant="body"
                    cx={{
                      mT: 'none',
                      mB: 'none',
                      display: 'flex',
                      alignItems: 'start',
                      spacing: '2xs',
                    }}>
                    <Txt
                      as="span"
                      style={{ width: '6.875rem', display: 'inline-block', flexShrink: 0 }}>
                      {item?.title}
                    </Txt>
                    <Txt as="span">{item?.price}</Txt>
                  </Txt>
                );
              })}
              {globals?.pricingInformation?.content && (
                <Box cx={{ pT: '2xs' }}>
                  <Txt html variant="small">
                    {pricing}
                  </Txt>
                </Box>
              )}
            </Box>
          ),
        }
      : undefined,
    venue && addressLine && addressFull
      ? {
          label: 'Venue',
          value: [
            <Flex key="event_venue" spacing="3xs" direction="column">
              <Txt variant="body" cx={{ m: 'none' }}>
                {venue.title}
              </Txt>
              <Btn
                variant="text"
                as="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  addressFull
                )}`}
                underline="always"
                color="black"
                wrap
                target="_blank"
                fullWidth={false}
                cx={{ textAlign: 'left', width: 'fit' }}>
                {addressLine}
              </Btn>
              {venueRefUri ? (
                <Btn
                  variant="text"
                  underline="always"
                  color="black"
                  wrap
                  cx={{ textAlign: 'left', width: 'fit' }}
                  href={venueRefUri}>
                  {t('actions.venueInfo')}
                </Btn>
              ) : undefined}
            </Flex>,
          ],
        }
      : undefined,
    ...eventInfo.map((item, index) => {
      return {
        label: `${item?.heading}`,
        value: [
          <Txt key={index} html>
            {item?.content}
          </Txt>,
        ],
      };
    }),
    // Shortlist button
    {
      value: (
        <Btn
          key="event_shortlist"
          fullWidth
          color="black"
          startIcon={isShortlisted ? 'heart' : 'heartOutlined'}
          weight="bold"
          style={{
            fontSize: typography.size.small,
          }}
          onClick={() => toggleEvent(card)}>
          {shortlistButtonText}
        </Btn>
      ),
    },
  ];

  return <EntryMeta cx={{ mY: '3xl' }} metaValues={meta} />;
};

export default EventLayoutSidebar;
